package er.prateek.crossly.progression.achievements

/** Drives the medal's colour and how loud the unlock celebration is. */
enum class AchievementRarity { COMMON, RARE, EPIC, LEGENDARY }

/**
 * Every badge the player can earn. Deliberately milestones, not per-game confetti: firsts per
 * mode, streak landmarks, volume landmarks, and a few "you played the game the way we hoped"
 * moments (all three modes, a full Brainstorm round, saving a streak).
 *
 * [id] is persisted and sent to analytics -- never rename it. Display copy lives in
 * `ui/achievements` so this stays resource-free and unit-testable.
 */
enum class Achievement(val id: String, val rarity: AchievementRarity, val unlockedBy: (PlayerStats) -> Boolean) {
    FIRST_WORDS("first_words", AchievementRarity.COMMON, { it.wordSolved >= 1 }),
    FIRST_NUMBERS("first_numbers", AchievementRarity.COMMON, { it.mathSolved >= 1 }),
    FIRST_RIDDLES("first_riddles", AchievementRarity.COMMON, { it.riddleSolved >= 1 }),
    FIRST_DAILY("first_daily", AchievementRarity.COMMON, { it.dailySolved }),
    FLAWLESS("flawless", AchievementRarity.COMMON, { it.perfectSolves >= 1 }),
    STREAK_3("streak_3", AchievementRarity.COMMON, { it.longestStreak >= 3 }),
    TRIPLE_THREAT("triple_threat", AchievementRarity.RARE, { it.wordSolved >= 1 && it.mathSolved >= 1 && it.riddleSolved >= 1 }),
    STREAK_7("streak_7", AchievementRarity.RARE, { it.longestStreak >= 7 }),
    SOLVED_10("solved_10", AchievementRarity.RARE, { it.totalSolved >= 10 }),
    BRAINSTORMER("brainstormer", AchievementRarity.RARE, { it.brainstormRounds >= 1 }),
    PHOENIX("phoenix", AchievementRarity.RARE, { it.streakRestores >= 1 }),
    PERFECTIONIST("perfectionist", AchievementRarity.EPIC, { it.perfectSolves >= 25 }),
    SOLVED_50("solved_50", AchievementRarity.EPIC, { it.totalSolved >= 50 }),
    STREAK_30("streak_30", AchievementRarity.EPIC, { it.longestStreak >= 30 }),
    SOLVED_150("solved_150", AchievementRarity.LEGENDARY, { it.totalSolved >= 150 }),
    STREAK_100("streak_100", AchievementRarity.LEGENDARY, { it.longestStreak >= 100 }),
    SOLVED_5("solved_5", AchievementRarity.COMMON, { it.totalSolved >= 5 }),
    SOLVED_25("solved_25", AchievementRarity.RARE, { it.totalSolved >= 25 }),
    SOLVED_300("solved_300", AchievementRarity.LEGENDARY, { it.totalSolved >= 300 }),
    SOLVED_500("solved_500", AchievementRarity.LEGENDARY, { it.totalSolved >= 500 }),
    WORDS_10("words_10", AchievementRarity.RARE, { it.wordSolved >= 10 }),
    WORDS_50("words_50", AchievementRarity.EPIC, { it.wordSolved >= 50 }),
    NUMBERS_10("numbers_10", AchievementRarity.RARE, { it.mathSolved >= 10 }),
    NUMBERS_50("numbers_50", AchievementRarity.EPIC, { it.mathSolved >= 50 }),
    RIDDLES_10("riddles_10", AchievementRarity.RARE, { it.riddleSolved >= 10 }),
    RIDDLES_50("riddles_50", AchievementRarity.EPIC, { it.riddleSolved >= 50 }),
    POLYMATH("polymath", AchievementRarity.EPIC, { it.wordSolved >= 10 && it.mathSolved >= 10 && it.riddleSolved >= 10 }),
    PERFECT_5("perfect_5", AchievementRarity.RARE, { it.perfectSolves >= 5 }),
    PERFECT_100("perfect_100", AchievementRarity.LEGENDARY, { it.perfectSolves >= 100 }),
    STREAK_14("streak_14", AchievementRarity.RARE, { it.longestStreak >= 14 }),
    STREAK_60("streak_60", AchievementRarity.EPIC, { it.longestStreak >= 60 }),
    STREAK_365("streak_365", AchievementRarity.LEGENDARY, { it.longestStreak >= 365 }),
    BRAINSTORM_5("brainstorm_5", AchievementRarity.EPIC, { it.brainstormRounds >= 5 }),
    BRAINSTORM_25("brainstorm_25", AchievementRarity.LEGENDARY, { it.brainstormRounds >= 25 }),
    PHOENIX_3("phoenix_3", AchievementRarity.EPIC, { it.streakRestores >= 3 }),
    ;

    companion object {
        fun fromId(id: String): Achievement? = entries.firstOrNull { it.id == id }
    }
}

/** Everything the badge rules look at, gathered from the progress managers in one place. */
data class PlayerStats(
    val wordSolved: Int = 0,
    val mathSolved: Int = 0,
    val riddleSolved: Int = 0,
    val dailySolved: Boolean = false,
    val perfectSolves: Int = 0,
    val longestStreak: Int = 0,
    val brainstormRounds: Int = 0,
    val streakRestores: Int = 0,
) {
    val totalSolved: Int get() = wordSolved + mathSolved + riddleSolved
}
